import { useDynamicQueryParams } from "@/hooks/useDynamicQueryParams";
import { Filters, mergeFilters } from "@/lib/filters";
import { APIService, SuccessFetchListResponse } from "@/services/api";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { useCreateDataTableOptions } from "./useCreateDataTableOptions";

const useQueryCreatorForAPIService = <T extends { id: string }>(
  name: string,
  service: APIService<T>,
  defaultFilters?: Filters<T>,
) => {
  const options = useCreateDataTableOptions<T>(name);

  const queryKey = useDynamicQueryParams(name, options);

  const filters = useMemo(
    () => mergeFilters(defaultFilters, options.filters),
    [defaultFilters, options.filters],
  );

  const queryFn = useCallback(
    ({ pageParam }: { pageParam: string | undefined }) =>
      service.getList({
        lastItem: pageParam,
        filters,
        sorting: options.sorting[0],
      }),
    [service, filters, options.sorting],
  );

  const getNextPageParam = useCallback(
    (lastPage: SuccessFetchListResponse<T>) => lastPage.last_item,
    [],
  );

  const query = useInfiniteQuery({
    queryKey,
    queryFn,
    getNextPageParam,
    initialPageParam: undefined,
  });

  return useMemo(() => ({ query, options }), [query, options]);
};

export default useQueryCreatorForAPIService;
